import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import {
  AccountDetailEmpId as EmpId,
  AccountDetailNameFirst as NameFirst,
  AccountDetailNameLast as NameLast,
  AccountDetailValidFrom as ValidFrom,
  AccountDetailValidTo as ValidTo
} from './AccountDetailsFormFields';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import { FormHelperText, Grid, TextField as MuiTextField } from '@mui/material';
import { AccountGroupsTable } from './AccountGroupsTable';
import React from 'react';
import { makeStyles } from '@material-ui/core';
import { useGetLinkedEmployeeOptions } from './hooks/useGetLinkedEmployeeOptions';
import { AccountDetails } from '../../../types';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import { converterForDate } from 'utils/date';

const EmpIdWithReadMode = withReadModeField(EmpId);
const NameFirstWithReadMode = withReadModeField(NameFirst);
const NameLastWithReadMode = withReadModeField(NameLast);
const ValidFromWithReadMode = withReadModeField(ValidFrom);
const ValidToWithReadMode = withReadModeField(ValidTo);

const useStyles = makeStyles(() => ({
  root: {
    flexDirection: 'column',
    flex: 1
  }
}));
const renderCompensationTypeIdReadMode =
  (options: Option[]) =>
  ({ name, label }) => {
    const { getValues } = useFormContext();

    const foundOption = options?.find(
      (option) => option.value === getValues(name)
    );

    return (
      <MuiTextField
        variant="outlined"
        label={label}
        name={name}
        value={foundOption?.label}
        fullWidth
        disabled={true}
        size="small"
        className="read_mode"
        defaultValue={'-'}
      />
    );
  };

interface Props {
  originalDetails: AccountDetails;
}

export const AccountFormFields: React.FC<Props> = ({ originalDetails }) => {
  const { t } = useTranslation('common');
  const classes = useStyles();
  const { control, watch } = useFormContext<AccountDetails>();
  const { data: options } = useGetLinkedEmployeeOptions(true);
  const from = watch('employee.valid.from');
  const to = watch('employee.valid.to');

  return (
    <Box display="flex" className={classes.root}>
      <Box mb={2.5} sx={{ width: '100%' }}>
        <Grid container mb={2.5} rowSpacing={2} columnSpacing={2}>
          <Grid xs={6} item>
            <MuiTextField
              variant="outlined"
              label={t('accountName')}
              value={watch('accountName')}
              fullWidth
              disabled={true}
              size="small"
              className="read_mode"
            />
          </Grid>
          <Grid xs={6} item>
            <MuiTextField
              variant="outlined"
              label={t('email')}
              value={watch('comEmail')}
              fullWidth
              disabled={true}
              size="small"
              className="read_mode"
            />
          </Grid>
          <Grid xs={6} item>
            {originalDetails.empId ? (
              <>
                <MuiTextField
                  variant="outlined"
                  sx={{ paddingBottom: from || to ? 0 : 'unset' }}
                  label={t('linkedEmployee')}
                  value={watch('employee.name')}
                  fullWidth
                  disabled={true}
                  size="small"
                  className="read_mode"
                />
                {(from || to) && (
                  <FormHelperText sx={{ paddingLeft: 1.75 }}>{`${t(
                    'validFrom'
                  )} ${converterForDate(from)} ${t(
                    'validTo'
                  )} ${converterForDate(to)}`}</FormHelperText>
                )}
              </>
            ) : (
              <EmpIdWithReadMode
                options={options}
                label={t('linkedEmployee')}
                control={control}
                name={'empId'}
                readModeRenderer={renderCompensationTypeIdReadMode(
                  options ?? []
                )}
              />
            )}
          </Grid>
          <Grid xs={6} item>
            <NameFirstWithReadMode
              label={t('firstName')}
              control={control}
              name={'nameFirst'}
            />
          </Grid>
          <Grid xs={6} item>
            <NameLastWithReadMode
              label={t('lastName')}
              control={control}
              name={'nameLast'}
            />
          </Grid>
          <Grid xs={3} item>
            <ValidFromWithReadMode
              label={t('validFrom')}
              control={control}
              name={'valid.from'}
            />
          </Grid>
          <Grid xs={3} item>
            <ValidToWithReadMode
              label={t('validTo')}
              control={control}
              name={'valid.to'}
            />
          </Grid>
        </Grid>
      </Box>

      <AccountGroupsTable />
    </Box>
  );
};
